/**
 * Hard-stop camera/microphone after a video/voice call.
 * Chrome's tab "recording" indicator stays on if any MediaStreamTrack is live.
 */
import {
  registerFromPublisher,
  stopAllRegisteredMedia,
} from "./mediaTrackRegistry";
import { getLiveCallMedia, clearLiveCallMedia } from "./callMediaHandle";

function stopTracksOnStream(stream) {
  if (!stream) return;
  try {
    if (typeof stream.getTracks === "function") {
      stream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch (_) {
          /* ignore */
        }
      });
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {object} [opts]
 * @param {object} [opts.publisher] OpenTok publisher
 * @param {object} [opts.session] OpenTok session
 */
export function stopAllCallMedia(opts = {}) {
  const live = getLiveCallMedia();
  const publisher = opts.publisher || live.publisher;
  const session = opts.session || live.session;

  // Mute first (helps OT release hardware on some browsers)
  try {
    if (publisher) {
      try {
        publisher.publishVideo?.(false);
      } catch (_) {
        /* ignore */
      }
      try {
        publisher.publishAudio?.(false);
      } catch (_) {
        /* ignore */
      }
      // Ensure registry knows about any last stream
      registerFromPublisher(publisher);
    }
  } catch (_) {
    /* ignore */
  }

  try {
    if (publisher) {
      try {
        if (typeof publisher.getMediaStream === "function") {
          stopTracksOnStream(publisher.getMediaStream());
        }
      } catch (_) {
        /* ignore */
      }
      try {
        session?.unpublish?.(publisher);
      } catch (_) {
        /* ignore */
      }
      try {
        if (typeof publisher.destroy === "function") publisher.destroy();
      } catch (_) {
        /* ignore */
      }
    }
  } catch (_) {
    /* ignore */
  }

  try {
    if (session && typeof session.disconnect === "function") {
      session.disconnect();
    }
  } catch (_) {
    /* ignore */
  }

  // Stop every track we ever registered + any remaining DOM media
  stopAllRegisteredMedia();
  clearLiveCallMedia();

  // OpenTok destroy() is async. Chrome keeps the tab recording indicator
  // until every MediaStreamTrack in this document is ended, so sweep again
  // after OT has had a chance to rebuild leftover publisher/subscriber tracks.
  if (typeof window !== "undefined") {
    [300, 1200, 2500].forEach((ms) => {
      window.setTimeout(() => {
        try {
          stopAllRegisteredMedia();
        } catch (_) {
          /* ignore */
        }
      }, ms);
    });
  }
}

export default stopAllCallMedia;
